/**
 * LeadMobileHeader Component - Mobile-optimized header with search and filters
 * Sticky search bar, status filter chip, date filter, and New Lead entry
 */

import React from "react";
import { Box, TextField, Chip, InputAdornment } from "@mui/material";
import { Search } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import NewLeadEntryButton from "./NewLeadEntryButton.jsx";
import LeadsDateFilter from "./LeadsDateFilter.jsx";

const StickyHeader = styled(Box)({
  position: "sticky",
  top: 0,
  zIndex: 100,
  padding: "12px 16px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
});

const HeaderRow = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  marginBottom: "8px",
});

const SearchField = styled(TextField)({
  flex: 1,
});

const FilterRow = styled(Box)({
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "center",
});

const FilterChip = styled(Chip)({
  fontWeight: 500,
});

const statusOptions = [
  { value: "new", label: "New" },
  { value: "marketplace", label: "On Marketplace" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "converted", label: "Converted" },
  { value: "all", label: "All" },
];

/**
 * LeadMobileHeader Component
 * @param {Object} props
 * @param {string} props.searchValue - Current search value
 * @param {Function} props.onSearchChange - Callback when search changes
 * @param {string} props.filterStatus - Current status filter
 * @param {Function} props.onFilterChange - Callback when filter changes
 * @param {Object} [props.dateFilter] - Current date filter
 * @param {Function} [props.onDateFilterChange] - Callback when date filter changes
 */
const LeadMobileHeader = ({
  searchValue = "",
  onSearchChange,
  filterStatus = "new",
  onFilterChange,
  dateFilter,
  onDateFilterChange,
}) => {
  const handleFilterClick = (status) => {
    if (onFilterChange) {
      onFilterChange(status);
    }
  };

  return (
    <StickyHeader
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.12)",
      }}
    >
      <HeaderRow>
        <SearchField
          fullWidth
          size="small"
          placeholder="Search leads..."
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
            },
          }}
        />
        <NewLeadEntryButton size="small" />
      </HeaderRow>

      <FilterRow>
        {statusOptions.map((option) => (
          <FilterChip
            key={option.value}
            label={option.label}
            onClick={() => handleFilterClick(option.value)}
            size="small"
            variant={filterStatus === option.value ? "filled" : "outlined"}
            sx={{
              ...(filterStatus === option.value && {
                backgroundColor: "#1976d2",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
              }),
            }}
          />
        ))}
      </FilterRow>

      {onDateFilterChange ? (
        <FilterRow sx={{ mt: 1 }}>
          <LeadsDateFilter
            value={dateFilter}
            onChange={onDateFilterChange}
            minWidth={160}
          />
        </FilterRow>
      ) : null}
    </StickyHeader>
  );
};

export default LeadMobileHeader;
